import React, { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import {
    Dialog, DialogClose, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const REASONS = [
    { v: "stolen", l: "Stolen / fake — this isn't the candidate's own photo" },
    { v: "mismatch", l: "Doesn't match the candidate's name or profile" },
    { v: "inappropriate", l: "Inappropriate content (nudity, violence, hate)" },
    { v: "other", l: "Other (please specify)" },
];

/**
 * ReportPhotoDialog — flag a candidate's photo on an Open-for-Work listing.
 *
 * 3+ distinct reporters → backend auto-hides the photo until an admin
 * reviews. Idempotent per (listing, reporter) so spamming doesn't help.
 */
export default function ReportPhotoDialog({ listingId, compact = false }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!reason) return toast.error("Please pick a reason");
        if (reason === "other" && !note.trim()) return toast.error("Tell us briefly what's wrong");
        setBusy(true);
        try {
            await api.post(`/listings/${listingId}/report-photo`, { reason, note: note.trim() || null });
            toast.success("Thanks — our team will review");
            setOpen(false);
            setReason("");
            setNote("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const onOpenChange = (next) => {
        if (next && !user) {
            navigate(`/login?redirect=/listing/${listingId}`);
            return;
        }
        setOpen(next);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    data-testid="report-photo-btn"
                    className={
                        compact
                            ? "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/85 hover:bg-white text-aduku-navy/70 hover:text-red-600 text-[10px] font-bold border border-aduku-border"
                            : "inline-flex items-center gap-1.5 text-[11px] font-bold text-aduku-navy/60 hover:text-red-600"
                    }
                    title="Report photo"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Flag className="w-3 h-3" /> Report photo
                </button>
            </DialogTrigger>
            <DialogContent data-testid="report-photo-dialog" onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>Report this candidate's photo</DialogTitle>
                    <DialogDescription>
                        We auto-hide photos that receive 3+ independent reports while our team reviews. Misuse may result in account action.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 mt-2">
                    {REASONS.map((r) => (
                        <label key={r.v} className="flex items-start gap-2.5 cursor-pointer p-2 rounded-lg border border-aduku-border hover:border-aduku-navy/40 hover:bg-aduku-bg/40">
                            <input
                                type="radio"
                                name="report-reason"
                                value={r.v}
                                checked={reason === r.v}
                                onChange={() => setReason(r.v)}
                                data-testid={`report-reason-${r.v}`}
                                className="mt-1 accent-aduku-yellow"
                            />
                            <span className="text-sm text-aduku-navy/85">{r.l}</span>
                        </label>
                    ))}
                </div>
                <Textarea
                    data-testid="report-photo-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={reason === "other" ? "What's wrong? (required for 'Other')" : "Additional context (optional)"}
                    rows={3}
                    maxLength={300}
                    className="mt-3"
                />
                <DialogFooter className="gap-2">
                    <DialogClose asChild>
                        <Button variant="ghost" className="h-9">Cancel</Button>
                    </DialogClose>
                    <Button onClick={submit} disabled={busy} data-testid="report-photo-submit" className="bg-red-600 hover:bg-red-700 text-white h-9">
                        {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : <>Submit report</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
